import { getLanguage, location } from 'cdui-js';
import i18n from '../../../i18n';
import {
  appendDepositAppQuery,
  applyDepositAppQuery,
  snapshotDepositAppQuery,
} from '../deposit/h5-deposit/appBridge';
import type {
  StockTransferInApplyRecord,
  StockTransferInApplySaveBody,
  StockTransferInApplySubmitBody,
  StockTransferInSettlement,
  StockTransferInStockItem,
  StockTransferInStockItemResp,
  StockTransferInWfStatus,
  StockTransferOutBroker,
} from '../../../api/stockTransferIn';
import { parseServerTimeMs } from '../../../lib/date';
import { userState } from '../../../stores/user';
import inboundBrokerLogoUrl from '../../../../icons/my/logo-about-us.png';
import { readNameParts } from '../account/nameParts';
import {
  accountLabel,
  accountNumberOf,
  isSecuritiesAccount,
  overviewAccountsOf,
  sortAccounts,
} from '../currency-exchange/shared';

export type StockTransferMarket = 'HK' | 'US';

export type StatementProofKind = 'image' | 'pdf';

export type StatementProof = {
  localUrl: string;
  filePath: string;
  /** 缩略图/预览分叉；草稿回填时可由 path 扩展名推断 */
  kind: StatementProofKind;
};

/** 由路径扩展名推断结单类型（无扩展名时默认 image） */
export const statementProofKindFromPath = (filePath: string): StatementProofKind => {
  const name = String(filePath || '').trim().split(/[/\\]/).pop() || '';
  return /\.pdf$/i.test(name) ? 'pdf' : 'image';
};

/** 转股列表单项（本地编辑态） */
export type StockTransferLineItem = {
  id: string;
  /** 服务端明细 ID（stock/save、batchSave 返回） */
  serverStockId?: number;
  stockCode: string;
  marketId: string;
  stockName: string;
  marketType?: string;
  quantity: number;
  selected: boolean;
};

export type StockTransferDraft = {
  /** 草稿申请单 ID（apply/save 返回） */
  /** 申请单 ID（int64，内存态用字符串） */
  applyId?: string;
  marketCode: StockTransferMarket;
  outBrokerId: number;
  outBrokerName: string;
  outBrokerIsOther: boolean;
  outBrokerAccountNo: string;
  /** 转出方账户名称（英文名 || 中文名，同开户银行户名规则） */
  outBrokerAccountName: string;
  outBrokerContactPerson: string;
  outBrokerContactPhone: string;
  /** 转出券商清单 contactPhone（与用户填写区分，致电优先用此项） */
  outBrokerCatalogContactPhone?: string;
  outBrokerCustomName: string;
  inBrokerName: string;
  inCcassId: string;
  inDtcNo: string;
  /** 展示用账户类型文案 */
  inAccountType: string;
  /** 提交接口 CASH / MARGIN */
  inAccountTypeCode: string;
  /** 提交接口 inAccountNum */
  inAccountNum: string;
  settlementAccountNo: string;
  statementProofs: string[];
  stocks: StockTransferLineItem[];
};

let draft: StockTransferDraft | null = null;
/** 确认页 submit 成功后、签名页使用的申请单 ID（与 draft.applyId 同步） */
let activeApplyId: string | null = null;

/** 解析/规范化申请单 ID（接口 int64 → 全程字符串，禁止 Number()） */
export const normalizeStockTransferApplyId = (id: unknown): string => {
  if (id == null || id === '') return '';
  if (typeof id === 'string') {
    const s = id.trim();
    return /^\d+$/.test(s) && !/^0+$/.test(s) ? s : '';
  }
  if (typeof id === 'number') {
    if (!Number.isFinite(id) || id <= 0) return '';
    return String(Math.trunc(id));
  }
  return normalizeStockTransferApplyId(String(id));
};

export const displayTransferField = (raw?: string | null) => {
  const v = String(raw ?? '').trim();
  return v || '--';
};

/**
 * 转股转出方账户姓名：与开户银行户名一致 —— 英文名(名+姓)优先，无英文再中文。
 * 中文名可能为空；不用昵称/登录名。读前请 awaitUserInfoReady（全局 userInfo）。
 */
export const stockTransferUserAccountFullName = (): string => {
  const info = userState.userInfo as Record<string, unknown> | null;
  const userMsg = (info?.userMsg ?? {}) as Record<string, unknown>;
  const userBase = (info?.userBase ?? {}) as Record<string, unknown>;
  const names = readNameParts({
    fullName: String(userMsg.fullName ?? userBase.fullName ?? ''),
    pinyinName: String(userMsg.pinyinName ?? userBase.pinyinName ?? ''),
    firstNameEn: String(userBase.firstNameEn ?? userMsg.firstNameEn ?? ''),
    lastNameEn: String(userBase.lastNameEn ?? userMsg.lastNameEn ?? ''),
    lastNameCn: String(userBase.lastNameCn ?? userMsg.lastNameCn ?? ''),
    firstNameCn: String(userBase.firstNameCn ?? userMsg.firstNameCn ?? ''),
  });
  return names.pinyinName || names.fullName || '';
};

/** 致电转出方：券商清单电话 → 用户填写的联系人电话 */
export const resolveOutBrokerDialPhone = (
  draft: Pick<StockTransferDraft, 'outBrokerCatalogContactPhone' | 'outBrokerContactPhone'> | null | undefined,
): string => {
  const fromCatalog = String(draft?.outBrokerCatalogContactPhone ?? '').trim();
  if (fromCatalog) return fromCatalog;
  return String(draft?.outBrokerContactPhone ?? '').trim();
};

export const defaultInAccountTypeCode = (market: StockTransferMarket) => (market === 'US' ? 'CASH' : 'MARGIN');

export type StockTransferInboundAccountOption = {
  accountNum: string;
  inAccountTypeCode: 'CASH' | 'MARGIN';
  /** ActionSheet 选项文案（类型 + 户号） */
  sheetLabel: string;
  /** 确认页「账户类型」展示文案 */
  typeLabel: string;
};

export const inAccountTypeCodeFromRowType = (type: unknown): 'CASH' | 'MARGIN' =>
  String(type ?? '').trim() === 'M' ? 'MARGIN' : 'CASH';

/** 确认页 / 提交用账户类型展示名（仅现金 / 孖展，与港美市场无关） */
export const displayInAccountTypeLabel = (code: 'CASH' | 'MARGIN', _market?: StockTransferMarket): string =>
  code === 'MARGIN' ? i18n.FundH5.AccountMargin : i18n.FundH5.AccountCash;

export const inAccountTypeCodeFromDraft = (draft: Pick<StockTransferDraft, 'inAccountTypeCode' | 'marketCode'>) => {
  const raw = String(draft.inAccountTypeCode || defaultInAccountTypeCode(draft.marketCode)).trim().toUpperCase();
  return raw === 'MARGIN' ? 'MARGIN' : 'CASH';
};

export const displayInAccountTypeForDraft = (
  draft: Pick<StockTransferDraft, 'inAccountTypeCode' | 'marketCode'> | null | undefined,
): string => {
  if (!draft) return '';
  return displayInAccountTypeLabel(inAccountTypeCodeFromDraft(draft), draft.marketCode);
};

/** 转入方可选证券户（现金 / 孖展） */
export const listInboundSecuritiesAccounts = (
  metaRes: unknown,
  market: StockTransferMarket,
): StockTransferInboundAccountOption[] =>
  sortAccounts(overviewAccountsOf(metaRes))
    .filter(isSecuritiesAccount)
    .map((row) => {
      const accountNum = accountNumberOf(row);
      const inAccountTypeCode = inAccountTypeCodeFromRowType(row?.type);
      return {
        accountNum,
        inAccountTypeCode,
        sheetLabel: accountLabel(row),
        typeLabel: displayInAccountTypeLabel(inAccountTypeCode, market),
      };
    })
    .filter((item) => item.accountNum);

export const pickInboundAccountOption = (
  accounts: StockTransferInboundAccountOption[],
  draft: StockTransferDraft,
): StockTransferInboundAccountOption | null => {
  if (!accounts.length) return null;
  const num = resolveInAccountNum(draft);
  if (num) {
    const byNum = accounts.find((item) => item.accountNum === num);
    if (byNum) return byNum;
  }
  const code = String(draft.inAccountTypeCode || defaultInAccountTypeCode(draft.marketCode)).trim();
  const byCode = accounts.find((item) => item.inAccountTypeCode === code);
  if (byCode) return byCode;
  return accounts[0];
};

export const applyInboundAccountToDraft = (
  draft: StockTransferDraft,
  option: StockTransferInboundAccountOption,
): StockTransferDraft => ({
  ...draft,
  inAccountNum: option.accountNum,
  settlementAccountNo: option.accountNum,
  inAccountTypeCode: option.inAccountTypeCode,
  inAccountType: option.typeLabel,
});

export const resolveInAccountNum = (draft: StockTransferDraft) => {
  const fromDraft = String(draft.inAccountNum || draft.settlementAccountNo || '').trim();
  if (fromDraft) return fromDraft;
  return String(userState.accountNumHK || '').trim();
};

export const parseStockTransferApplyId = (resp: { id?: unknown } | null | undefined) =>
  normalizeStockTransferApplyId(resp?.id);

const stockItemsFromLines = (lines: StockTransferLineItem[]): StockTransferInStockItem[] =>
  lines.map((item) => {
    const row: StockTransferInStockItem = {
      stockCode: item.stockCode,
      marketId: item.marketId,
      quantity: Math.floor(Number(item.quantity)),
    };
    const name = item.stockName?.trim();
    if (name) row.stockName = name;
    return row;
  });

/** 组装 apply/save 请求体（不含股票明细；股票走 batchSave） */
export const buildStockTransferApplySaveBody = (draft: StockTransferDraft): StockTransferInApplySaveBody | null => {
  if (!draft.outBrokerId) return null;
  const statementPaths = (draft.statementProofs || []).filter(Boolean).slice(0, 5);
  if (!statementPaths.length) return null;

  const marketCode = draft.marketCode;
  const inAccountNum = resolveInAccountNum(draft);
  const inAccountType = String(draft.inAccountTypeCode || defaultInAccountTypeCode(marketCode)).trim();
  const outBrokerAccountName = String(draft.outBrokerAccountName || stockTransferUserAccountFullName() || '').trim();

  const body: StockTransferInApplySaveBody = {
    id: draft.applyId,
    marketCode,
    outBrokerId: draft.outBrokerId,
    outBrokerAccountNo: draft.outBrokerAccountNo?.trim() || undefined,
    outBrokerAccountName: outBrokerAccountName || undefined,
    outBrokerContactPerson: draft.outBrokerContactPerson?.trim() || undefined,
    outBrokerContactPhone: draft.outBrokerContactPhone?.trim() || undefined,
    inBrokerName: draft.inBrokerName?.trim() || fixedInboundBrokerName(),
    statementFilePaths: statementPaths.join(','),
  };

  if (inAccountNum) {
    body.inAccountNum = inAccountNum;
    body.inAccountType = inAccountType;
  }
  if (draft.outBrokerIsOther && draft.outBrokerCustomName?.trim()) {
    body.outBrokerCustomName = draft.outBrokerCustomName.trim();
  }
  if (marketCode === 'HK') {
    body.inCcassId = resolveInboundCcassId(marketCode, null, draft.inCcassId);
  }

  return body;
};

/** 提交前本地校验（分步保存后 submit 仅传 id） */
export const validateStockTransferBeforeSubmit = (
  draft: StockTransferDraft,
  selected: StockTransferLineItem[],
): StockTransferInApplySubmitBody | null => {
  if (!selected.length) return null;

  const marketCode = draft.marketCode;
  const outBrokerAccountNo = String(draft.outBrokerAccountNo || '').trim();
  const outBrokerAccountName = String(draft.outBrokerAccountName || stockTransferUserAccountFullName() || '').trim();
  const inAccountNum = resolveInAccountNum(draft);
  const inAccountType = String(draft.inAccountTypeCode || defaultInAccountTypeCode(marketCode)).trim();

  if (!outBrokerAccountNo) return null;
  if (!outBrokerAccountName) return null;
  if (!inAccountNum) return null;

  const statementPaths = (draft.statementProofs || []).filter(Boolean).slice(0, 5);
  if (!statementPaths.length) return null;

  const applyId = draft.applyId ?? activeApplyId;
  if (applyId) {
    return { id: applyId };
  }

  const body: StockTransferInApplySubmitBody = {
    marketCode,
    outBrokerId: draft.outBrokerId,
    outBrokerAccountNo,
    outBrokerAccountName,
    outBrokerContactPerson: draft.outBrokerContactPerson?.trim() || undefined,
    outBrokerContactPhone: draft.outBrokerContactPhone?.trim() || undefined,
    inBrokerName: draft.inBrokerName?.trim() || fixedInboundBrokerName(),
    inAccountType,
    inAccountNum,
    statementFilePaths: statementPaths.join(','),
    stocks: stockItemsFromLines(selected),
  };

  if (draft.outBrokerIsOther && draft.outBrokerCustomName?.trim()) {
    body.outBrokerCustomName = draft.outBrokerCustomName.trim();
  }
  if (marketCode === 'HK') {
    body.inCcassId = resolveInboundCcassId(marketCode, null, draft.inCcassId);
  }

  return body;
};

/** @deprecated 请用 validateStockTransferBeforeSubmit */
export const buildStockTransferSubmitBody = validateStockTransferBeforeSubmit;

export const mergeStockLinesWithBatchSaveResp = (
  lines: StockTransferLineItem[],
  selected: StockTransferLineItem[],
  saved: StockTransferInStockItemResp[],
) => {
  const savedMap = new Map(saved.map((row) => [stockLineKey({ stockCode: String(row.stockCode || ''), marketId: String(row.marketId || '') }), row]));
  const selectedKeys = new Set(selected.map((item) => stockLineKey(item)));
  return lines.map((line) => {
    if (!selectedKeys.has(stockLineKey(line))) return line;
    const hit = savedMap.get(stockLineKey(line));
    const serverStockId = Number(hit?.id);
    if (!Number.isFinite(serverStockId) || serverStockId <= 0) return line;
    return { ...line, serverStockId };
  });
};

export const statementProofFileName = (filePath: string, index: number) => {
  const trimmed = String(filePath || '').trim();
  const base = trimmed.split(/[/\\]/).pop()?.trim();
  if (base) return base;
  return `${i18n.StockTransferIn.StatementFileFallback}${String(index + 1).padStart(2, '0')}`;
};

export const STOCK_TRANSFER_SETTLEMENT_SIGN_ATTACH = 'stock_transfer_in_settlement_instruction_signed';

export const setStockTransferApplyId = (id: unknown) => {
  const valid = normalizeStockTransferApplyId(id);
  activeApplyId = valid || null;
  if (valid && draft) {
    draft = { ...draft, applyId: valid };
  }
};

export const getStockTransferApplyId = (): string | null => {
  if (activeApplyId) return activeApplyId;
  const fromDraft = normalizeStockTransferApplyId(draft?.applyId);
  return fromDraft || null;
};

export type StockTransferSubmittedSnapshot = {
  applyId: string;
  submittedAt: number;
  market: StockTransferMarket;
  draft: StockTransferDraft;
  selectedStocks: StockTransferLineItem[];
  /** 工作流 / 申请状态；提交成功页默认 SUBMITTED */
  status?: StockTransferInWfStatus;
};

/** 转入记录列表 / 详情：工作流状态文案（与接口 status 一致） */
export const wfStatusLabel = (status?: StockTransferInWfStatus | string) => {
  const key = String(status || '').toUpperCase();
  const map: Record<string, string> = {
    SUBMITTED: i18n.StockTransferIn.WfStatusSubmitted,
    APPROVING: i18n.StockTransferIn.WfStatusApproving,
    BACK: i18n.StockTransferIn.WfStatusBack,
    REFUSED: i18n.StockTransferIn.WfStatusRefused,
    FINISHED: i18n.StockTransferIn.WfStatusFinished,
    CANCELLED: i18n.StockTransferIn.WfStatusCancelled,
    STOP: i18n.StockTransferIn.WfStatusStop,
  };
  return map[key] || i18n.StockTransferIn.WfStatusUnknown;
};

/** 记录详情顶栏：列表状态文案前加「申请」（列表仍用 {@link wfStatusLabel}） */
export const wfStatusDetailTitle = (status?: StockTransferInWfStatus | string) =>
  `${i18n.StockTransferIn.WfStatusDetailPrefix}${wfStatusLabel(status)}`;

export const isStockTransferInCancelledStatus = (status?: StockTransferInWfStatus | string) =>
  String(status || '').trim().toUpperCase() === 'CANCELLED';

/** 记录详情顶栏结果图标：成功绿勾 / 进行中·处理中蓝时钟 / 已取消灰叉 / 退回·拒绝·终止红叉 */
export type StockTransferStatusVisualKind = 'success' | 'pending' | 'cancelled' | 'error';

export const stockTransferStatusVisualKind = (
  status?: StockTransferInWfStatus | string,
): StockTransferStatusVisualKind => {
  const key = String(status || '').trim().toUpperCase();
  if (key === 'FINISHED') return 'success';
  if (key === 'CANCELLED') return 'cancelled';
  if (key === 'BACK' || key === 'REFUSED' || key === 'STOP') return 'error';
  // SUBMITTED / APPROVING / 未知码（文案「处理中」）/ 空态：均视为进行中
  return 'pending';
};

/** 是否可调用 cancel 接口（与 swagger 一致：DRAFT / SUBMITTED） */
export const canCancelStockTransferInApply = (status?: StockTransferInWfStatus | string) => {
  const key = String(status || '').trim().toUpperCase();
  if (!key) return true;
  return key === 'SUBMITTED' || key === 'DRAFT';
};

let submittedSnapshot: StockTransferSubmittedSnapshot | null = null;

export const setStockTransferSubmittedSnapshot = (snapshot: StockTransferSubmittedSnapshot) => {
  submittedSnapshot = snapshot;
};

export const getStockTransferSubmittedSnapshot = () => submittedSnapshot;

export const clearStockTransferSubmittedSnapshot = () => {
  submittedSnapshot = null;
};

const inAccountTypeCodeFromApi = (raw?: string): 'CASH' | 'MARGIN' => {
  const v = String(raw || '').trim().toUpperCase();
  return v === 'MARGIN' ? 'MARGIN' : 'CASH';
};

const parseMarketExtraDtcNo = (marketExtraJson?: string): string => {
  const trimmed = String(marketExtraJson || '').trim();
  if (!trimmed) return '';
  try {
    const extra = JSON.parse(trimmed) as Record<string, unknown>;
    const dtc = extra.dtcNo ?? extra.inDtcNo ?? extra.dtc;
    return String(dtc ?? '').trim();
  } catch {
    return '';
  }
};

/** 详情接口 → 成功页 / 记录详情展示快照 */
export const snapshotFromApplyDetail = (detail: StockTransferInApplyRecord): StockTransferSubmittedSnapshot => {
  const market = parseStockTransferMarket(detail.marketCode);
  const applyId = normalizeStockTransferApplyId(detail.id);
  const inAccountTypeCode = inAccountTypeCodeFromApi(detail.inAccountType);
  const selectedStocks: StockTransferLineItem[] = (detail.stocks || []).map((row, index) => ({
    id: String(row.id ?? index),
    serverStockId: row.id,
    stockCode: String(row.stockCode || '').trim(),
    marketId: String(row.marketId || defaultTransferMarketId(market)).trim(),
    stockName: String(row.stockName || '').trim(),
    quantity: Number(row.quantity) || 0,
    selected: true,
  }));

  const draftView: StockTransferDraft = {
    applyId: applyId || undefined,
    marketCode: market,
    outBrokerId: Number(detail.outBrokerId) || 0,
    outBrokerName: outBrokerNameFromApplyRecord(detail),
    outBrokerIsOther: detail.outBrokerIsOther === 1,
    outBrokerAccountNo: String(detail.outBrokerAccountNo || '').trim(),
    outBrokerAccountName: String(detail.outBrokerAccountName || '').trim(),
    outBrokerContactPerson: String(detail.outBrokerContactPerson || '').trim(),
    outBrokerContactPhone: String(detail.outBrokerContactPhone || '').trim(),
    outBrokerCatalogContactPhone: '',
    outBrokerCustomName: '',
    inBrokerName: String(detail.inBrokerName || '').trim() || fixedInboundBrokerName(),
    inCcassId: String(detail.inCcassId || '').trim(),
    inDtcNo: parseMarketExtraDtcNo(detail.marketExtraJson),
    inAccountType: displayInAccountTypeLabel(inAccountTypeCode, market),
    inAccountTypeCode,
    inAccountNum: String(detail.inAccountNum || '').trim(),
    settlementAccountNo: '',
    statementProofs: [],
    stocks: selectedStocks,
  };

  return {
    applyId: applyId || '',
    submittedAt: parseServerTimeMs(detail.createTime) || Date.now(),
    market,
    draft: draftView,
    selectedStocks,
    status: detail.status,
  };
};

export const readStockTransferQueryParam = (key: string): string => {
  const fromRouter = (location.query as Record<string, string> | undefined)?.[key];
  try {
    const fromUrl = new URLSearchParams(window.location.search).get(key);
    return (fromUrl || fromRouter || '').trim();
  } catch {
    return String(fromRouter || '').trim();
  }
};

export const clearStockTransferSession = () => {
  draft = null;
  activeApplyId = null;
  selectedOutBroker = null;
  otherOutBrokerDraft = null;
};

/** 选择页 → 表单页携带的转出券商 */
let selectedOutBroker: StockTransferOutBroker | null = null;

export const setSelectedOutBroker = (broker: StockTransferOutBroker) => {
  // 必须浅拷贝成普通对象：For/列表里的 reactive proxy 在选券商页卸载后不可再读（logo 等会丢）
  selectedOutBroker = broker ? { ...broker } : null;
};

export const getSelectedOutBroker = () => selectedOutBroker;

/** 「其他」转出方：仅所属转出方名称（中间页）；账户号码在后续表单页选填 */
export type OtherOutBrokerDraft = {
  customName: string;
};

let otherOutBrokerDraft: OtherOutBrokerDraft | null = null;

export const setOtherOutBrokerDraft = (next: OtherOutBrokerDraft) => {
  otherOutBrokerDraft = {
    customName: next.customName.trim(),
  };
};

export const getOtherOutBrokerDraft = () => otherOutBrokerDraft;

export const clearOtherOutBrokerDraft = () => {
  otherOutBrokerDraft = null;
};

export const brokerIdEquals = (a: unknown, b: number) => Number(a) === b;

export const readBrokerIdQuery = (): number => {
  const fromRouter = (location.query as Record<string, string> | undefined)?.brokerId;
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('brokerId');
    const raw = fromUrl || fromRouter || '';
    const id = Number(raw);
    return Number.isFinite(id) && id > 0 ? id : 0;
  } catch {
    const id = Number(fromRouter);
    return Number.isFinite(id) && id > 0 ? id : 0;
  }
};

export const getStockTransferDraft = () => draft;

export const setStockTransferDraft = (next: StockTransferDraft) => {
  draft = { ...next, stocks: next.stocks ?? [] };
};

export const createStockLineId = () => `st_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

export const getStockTransferLines = (): StockTransferLineItem[] => draft?.stocks ?? [];

export const setStockTransferLines = (lines: StockTransferLineItem[]) => {
  if (!draft) return;
  draft = { ...draft, stocks: lines };
};

export const stockLineKey = (item: Pick<StockTransferLineItem, 'stockCode' | 'marketId'>) =>
  `${item.marketId}:${item.stockCode}`;

export const TRANSFER_STOCK_NAME_MAX_LEN = 80;
export const TRANSFER_STOCK_CODE_MAX_LEN = 12;

/** 股票名称：仅截断长度，不滤字符 */
export function sanitizeTransferStockNameInput(raw: string): string {
  return String(raw ?? '').slice(0, TRANSFER_STOCK_NAME_MAX_LEN);
}

/** 股票代码输入：国际常见 ticker 字符，最长 12 */
export function sanitizeTransferStockCodeInput(raw: string): string {
  return String(raw ?? '')
    .replace(/[^A-Za-z0-9.\-_/]/g, '')
    .slice(0, TRANSFER_STOCK_CODE_MAX_LEN);
}

/** 转入数量：仅数字；多位时去掉前导 0（保留单个 "0" 作为输入中间态） */
export function sanitizeTransferQuantityInput(raw: string): string {
  let s = String(raw ?? '').replace(/\D/g, '');
  if (s.length > 1) s = s.replace(/^0+/, '');
  return s;
}

export const formatStockLineLabel = (item: Pick<StockTransferLineItem, 'stockName' | 'stockCode'>) => {
  const name = item.stockName?.trim() || '';
  const code = item.stockCode?.trim() || '';
  if (name && code && name !== code) return `${name} ${code}`;
  return name || code || '--';
};

/** 转股明细 marketId：用户手填股票名称与代码，市场由流程所选 HK/US 决定 */
export const defaultTransferMarketId = (market: StockTransferMarket) => market;

export const formatStockQuantity = (quantity: number) => {
  const n = Math.floor(Number(quantity));
  if (!Number.isFinite(n) || n <= 0) return '0';
  return n.toLocaleString('en-US');
};

export const marketTypeForTransfer = (market: StockTransferMarket) => (market === 'US' ? 'US' : 'HK');

export const readStockLineIdQuery = (): string => {
  const fromRouter = (location.query as Record<string, string> | undefined)?.lineId;
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('lineId');
    return (fromUrl || fromRouter || '').trim();
  } catch {
    return String(fromRouter || '').trim();
  }
};

export const parseStockTransferMarket = (raw?: string | null): StockTransferMarket => {
  return String(raw || '').toUpperCase() === 'US' ? 'US' : 'HK';
};

export const readMarketQuery = (): StockTransferMarket => {
  const fromRouter = (location.query as Record<string, string> | undefined)?.market;
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('market');
    return parseStockTransferMarket(fromUrl || fromRouter);
  } catch {
    return parseStockTransferMarket(fromRouter);
  }
};

/** 首屏 sync 外壳 theme / lang / safeArea（对齐兑换 applyDepositAppQuery） */
export const syncStockTransferAppShell = () => {
  applyDepositAppQuery(snapshotDepositAppQuery());
};

/** 站内跳转带上外壳 query（platform / lang / theme / riseFallMode / safeTop / safeBottom） */
export const withStockTransferQuery = (path: string, extra?: Record<string, string>) => {
  const params = new URLSearchParams();
  if (extra) {
    Object.entries(extra).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
  }
  appendDepositAppQuery(params);
  const query = params.toString();
  return query ? `${path}?${query}` : path;
};

export const brokerDisplayName = (broker: StockTransferOutBroker) => {
  const lang = String(getLanguage() || '');
  if (lang === 'en' || lang.startsWith('en')) {
    return broker.nameEn || broker.nameSc || broker.nameTc || '';
  }
  if (lang === 'zh-CN' || lang === 'zh-Hans') {
    return broker.nameSc || broker.nameTc || broker.nameEn || '';
  }
  return broker.nameTc || broker.nameSc || broker.nameEn || '';
};

/** 列表/详情中的转出券商名称（多语言字段） */
export const outBrokerNameFromApplyRecord = (row: {
  outBrokerNameEn?: string;
  outBrokerNameSc?: string;
  outBrokerNameTc?: string;
}) => {
  const lang = String(getLanguage() || '');
  if (lang === 'en' || lang.startsWith('en')) {
    return row.outBrokerNameEn || row.outBrokerNameSc || row.outBrokerNameTc || '';
  }
  if (lang === 'zh-CN' || lang === 'zh-Hans') {
    return row.outBrokerNameSc || row.outBrokerNameTc || row.outBrokerNameEn || '';
  }
  return row.outBrokerNameTc || row.outBrokerNameSc || row.outBrokerNameEn || '';
};

export const settlementCompanyName = (settlement: StockTransferInSettlement | null, fallback: string) => {
  return settlement?.companyName?.trim() || fallback;
};

export type InSettlementView = {
  brokerName: string;
  accountType: string;
  accountNo: string;
  ccassId: string;
  dtcNo: string;
};

/** 转入方展示：优先 settlement 接口字段，缺省用系统文案 */
/** 转入方 UI：UX App 图标（与「关于我们」一致；勿用 public/favicon.svg） */
export const INBOUND_BROKER_LOGO = inboundBrokerLogoUrl;

/** HK 转入 CCASS ID：settlement 接口无值时的兜底 */
export const INBOUND_CCASS_ID_FALLBACK = 'B01584';

export const fixedInboundBrokerName = () => i18n.StockTransferIn.DefaultInBroker;

/** HK：优先 draft → settlement.ccassId → 兜底 B01584 */
export const resolveInboundCcassId = (
  market: StockTransferMarket,
  settlement: StockTransferInSettlement | null,
  storedInCcassId?: string,
): string => {
  if (market !== 'HK') return '';
  const fromDraft = String(storedInCcassId ?? '').trim();
  if (fromDraft) return fromDraft;
  const fromApi = settlement?.ccassId?.trim();
  if (fromApi) return fromApi;
  return INBOUND_CCASS_ID_FALLBACK;
};

/** 确认页 / 成功页转入方 CCASS ID（HK/US 标签统一为 CCASS ID；US 用 draft.inCcassId） */
export const displayInboundCcassIdForTransfer = (
  market: StockTransferMarket,
  draft: Pick<StockTransferDraft, 'inCcassId'> | null | undefined,
): string => {
  if (market === 'HK') {
    return resolveInboundCcassId(market, null, draft?.inCcassId);
  }
  return String(draft?.inCcassId ?? '').trim();
};

export const buildInSettlementView = (
  settlement: StockTransferInSettlement | null,
  market: StockTransferMarket,
): InSettlementView => {
  const brokerName = settlementCompanyName(settlement, i18n.StockTransferIn.DefaultInBroker);
  const accountType =
    settlement?.accountName?.trim() ||
    settlement?.settlementAccountHint?.trim() ||
    (market === 'US' ? i18n.StockTransferIn.DefaultAccountTypeUs : i18n.StockTransferIn.DefaultAccountTypeHk);
  const accountNo = settlement?.accountNo?.trim() || '--';
  const ccassId =
    market === 'HK' ? resolveInboundCcassId(market, settlement) : settlement?.ccassId?.trim() || '--';
  const dtcNo = settlement?.dtcNo?.trim() || settlement?.dtcName?.trim() || '--';
  return { brokerName, accountType, accountNo, ccassId, dtcNo };
};
